/**
 * ThreadReturnChip — the conversation's persistent anchor ("one thread,
 * one search", 2026-07-07).
 *
 * When an active vibe conversation exists, criteria/map/saved surfaces show
 * one quiet chip: "💬 Your search · 2BR · East Austin · 12 options". Tapping
 * returns to the thread. Silent until summoned — the chip never speaks,
 * never grows, never toasts. Built as the component that would become the
 * chat-drawer trigger if the dock architecture ever lands: same placement,
 * same affordance, only the onClick changes.
 */
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useVibeStore } from '../../stores/vibeStore';
import { useTracking } from '../../hooks/useTracking';
import { useUiChromeStore } from '../../stores/uiChromeStore';

/** Surfaces where the chip appears. Not on /vibe (you're already there),
 *  not on home (the hero is the thread's front door). */
const SHOW_ON = ['/search', '/browse', '/my-list', '/price-report'];

function summarize(criteria: Record<string, unknown> | null, resultCount: number): string {
  const parts: string[] = [];
  const beds = (criteria?.bedrooms as string[]) || [];
  if (beds.length === 1) parts.push(beds[0].replace(/\s*bedrooms?/i, 'BR').replace(/^Studio$/i, 'Studio'));
  const hoods = (criteria?.neighborhoods as string[]) || [];
  if (hoods.length > 0) parts.push(hoods[0] + (hoods.length > 1 ? ` +${hoods.length - 1}` : ''));
  const budget = criteria?.budget_max as number | undefined;
  if (budget) parts.push(`budget $${budget.toLocaleString()}`);
  if (resultCount > 0) parts.push(`${resultCount} option${resultCount === 1 ? '' : 's'}`);
  return parts.slice(0, 3).join(' · ') || 'pick up where you left off';
}

export function ThreadReturnChip() {
  const location = useLocation();
  const navigate = useNavigate();
  const { track } = useTracking();
  const session = useVibeStore(s => (s.activeSessionId ? s.sessions[s.activeSessionId] : null));
  const floatsSuppressed = useUiChromeStore(s => s.floatsSuppressed);

  if (floatsSuppressed) return null;
  if (!SHOW_ON.some(p => location.pathname.startsWith(p))) return null;
  // Only a real conversation earns the chip: at least one user turn.
  if (!session || !session.messages.some(m => m.role === 'user')) return null;

  const summary = summarize(session.persistedCriteria, session.persistedResults.length);

  return (
    <button
      onClick={() => {
        track('thread_return', location.pathname.replace('/', '') || 'unknown', {
          result_count: session.persistedResults.length,
        });
        navigate('/vibe');
      }}
      className="fixed bottom-20 md:bottom-6 left-4 z-50 inline-flex items-center gap-2 max-w-[70vw] rounded-full pl-3 pr-4 h-11 transition-all hover:-translate-y-px active:scale-[0.97]"
      style={{
        background: 'var(--ts-bg-elevated, #1F1D26)',
        border: '1px solid rgba(255,210,77,0.4)',
        boxShadow: '0 0 18px -6px var(--ts-accent-glow), var(--ts-shadow-md)',
        color: 'var(--ts-text-primary)',
      }}
      aria-label={`Continue your chat: ${summary}`}
      data-testid="thread-return-chip"
    >
      <span aria-hidden className="text-base leading-none">💬</span>
      <span className="min-w-0 truncate text-left">
        <span className="block text-[9px] font-bold uppercase" style={{ fontFamily: 'var(--ts-font-mono)', letterSpacing: '0.12em', color: 'var(--ts-accent-primary)' }}>
          Your search
        </span>
        <span className="block text-xs truncate" style={{ color: 'var(--ts-text-secondary)' }}>{summary}</span>
      </span>
    </button>
  );
}
